import React from 'react';
import { AlertTriangle, FileSearch, FileText, HelpCircle, Info, Lock } from 'lucide-react';

/**
 * Corps de l'exemple de rapport — planche 4 de la maquette « Refonte conversion ».
 *
 * N'est plus une section de la landing : il est monté dans ExempleAnalyseModal,
 * derrière le bouton « Voir un exemple d'analyse » du hero. La page était passée à
 * 13 écrans sur mobile, dont 3 pour ce seul bloc.
 *
 * Intention de la maquette, et la raison d'être de la section : sur 154 dossiers
 * pour 7 payés, la conversion se joue ici. Le visiteur doit voir la valeur du
 * gratuit ET la nature du payant AVANT de déposer quoi que ce soit. D'où un
 * diagnostic montré en entier sur un dossier fictif, puis une frontière explicite
 * et le plan d'action flouté.
 *
 * Le contenu juridique du dossier d'exemple reste à faire relire (cf. annotation
 * de la planche).
 */

const SURVEILLER = [
  "Le montant de 412,50 € est présenté en bloc, sans ventilation entre la somme d'origine et les frais de recouvrement ajoutés. Or, en principe, les frais de recouvrement amiable restent à la charge du créancier (art. L.111-8 CPCE).",
  "Aucune facture, aucun contrat et aucun relevé n'a été joint au courrier. Le cabinet doit pouvoir justifier de l'existence et du montant de la créance s'il vous la réclame.",
  "Le courrier emploie le mot « saisie » sans préciser qu'une décision de justice serait nécessaire. Cette formulation est une pression, pas une étape de procédure.",
];

const FLOU = [
  "La date d'émission de la facture d'origine est inconnue. C'est pourtant elle qui fait courir le délai de prescription — en principe deux ans pour une créance d'un professionnel envers un consommateur (art. L.218-2 du code de la consommation).",
  "On ignore si le délai a déjà été interrompu. Contrairement à une idée répandue, une simple lettre recommandée n'y suffit pas : il faut une action en justice, une mesure d'exécution forcée, ou une reconnaissance de dette de votre part.",
];

const DOCUMENTS = [
  "La facture d'origine, avec sa date d'émission",
  'Le contrat de fourniture signé',
  'Le premier courrier reçu, avec sa date',
  'Vos derniers relevés de paiement',
];

const Bloc = ({ icon: Icon, titre, compteur, iconColor, children }) => (
  <div className="flex flex-col gap-3">
    <div className="flex items-center gap-[9px]">
      <Icon size={18} className="shrink-0" style={{ color: iconColor }} />
      <div className="text-[15px] font-bold sm:text-base" style={{ color: '#1e293b' }}>{titre}</div>
      {compteur && (
        <div
          className="rounded-full px-2 py-[2px] text-[11.5px] font-semibold"
          style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}
        >
          {compteur}
        </div>
      )}
    </div>
    {children}
  </div>
);

const Puce = ({ n, children }) => (
  <div className="flex gap-3">
    <span
      className="mt-[1px] shrink-0 text-xs font-bold tabular-nums"
      style={{ color: '#94a3b8' }}
    >
      {String(n).padStart(2, '0')}
    </span>
    <span className="text-[13.5px] leading-relaxed sm:text-sm" style={{ color: '#475569' }}>
      {children}
    </span>
  </div>
);

const ApercuRapport = ({ onCTAClick }) => (
  <div className="flex flex-col items-center gap-5">
      {/* Le rapport */}
      <div
        className="w-full overflow-hidden rounded-2xl border bg-white"
        style={{ borderColor: '#e2e8f0', boxShadow: '0 24px 48px -16px rgba(15, 23, 42, 0.14)' }}
      >
        {/* Tête */}
        <div
          className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-5 sm:px-8 sm:py-6"
          style={{ borderColor: '#f1f5f9' }}
        >
          <div className="flex items-center gap-4">
            <div className="relative h-[62px] w-[110px] shrink-0">
              <svg width="110" height="62" viewBox="0 0 110 62">
                <path d="M12 56 A43 43 0 0 1 98 56" fill="none" stroke="#e2e8f0" strokeWidth="10" strokeLinecap="round" />
                <path
                  d="M12 56 A43 43 0 0 1 98 56"
                  fill="none" stroke="#16a34a" strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={`${Math.PI * 43 * 0.68} ${Math.PI * 43}`}
                />
              </svg>
              <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center">
                <div className="text-[24px] font-extrabold leading-none" style={{ color: '#15803d' }}>
                  68<span className="text-[13px] font-bold">%</span>
                </div>
                <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#64748b' }}>
                  Contestable
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-base font-bold sm:text-[17px]" style={{ color: '#1e3a8a' }}>
                Diagnostic de votre dossier
              </div>
              <div className="text-[12px] sm:text-[13px]" style={{ color: '#64748b' }}>
                Dossier #DEMO · 1 document analysé · 2&nbsp;min 14&nbsp;s
              </div>
            </div>
          </div>
          <div
            className="flex items-center gap-[6px] rounded-full px-3 py-[6px] text-[12.5px] font-semibold"
            style={{ backgroundColor: '#dcfce7', color: '#15803d' }}
          >
            <FileSearch size={14} />
            Analyse gratuite
          </div>
        </div>

        {/* Corps gratuit */}
        <div className="flex flex-col gap-7 px-5 py-6 sm:gap-8 sm:px-8 sm:py-7">
          <Bloc icon={FileText} titre="Résumé de votre situation" iconColor="#1e3a8a">
            <div className="text-[13.5px] leading-relaxed sm:text-[14.5px]" style={{ color: '#475569' }}>
              Vous avez reçu une mise en demeure d'un cabinet de recouvrement qui réclame{' '}
              <strong className="font-semibold" style={{ color: '#1e293b' }}>412,50&nbsp;€</strong> pour le
              compte d'un fournisseur d'énergie. C'est le deuxième courrier reçu. Le cabinet annonce
              un «&nbsp;transfert au contentieux&nbsp;» sous 8 jours, mais aucune procédure judiciaire
              n'est engagée à ce stade — un cabinet de recouvrement ne peut rien saisir sans titre
              exécutoire, et seul un commissaire de justice peut exécuter. Le montant réclamé n'est
              accompagné d'aucune facture.
            </div>
          </Bloc>

          <Bloc icon={AlertTriangle} titre="À surveiller dans votre dossier" compteur="3 points" iconColor="#b45309">
            <div className="flex flex-col gap-[10px]">
              {SURVEILLER.map((t, i) => <Puce key={i} n={i + 1}>{t}</Puce>)}
            </div>
          </Bloc>

          <Bloc icon={HelpCircle} titre="Ce qui reste flou pour l'instant" compteur="2 points" iconColor="#1d4ed8">
            <div className="flex flex-col gap-[10px]">
              {FLOU.map((t, i) => <Puce key={i} n={i + 1}>{t}</Puce>)}
            </div>
          </Bloc>

          <Bloc icon={FileText} titre="Documents à retrouver pour renforcer votre dossier" iconColor="#475569">
            <div className="grid gap-2 sm:grid-cols-2">
              {DOCUMENTS.map((d) => (
                <div
                  key={d}
                  className="rounded-lg border px-3 py-[10px] text-[13px]"
                  style={{ backgroundColor: '#f8fafc', borderColor: '#e2e8f0', color: '#475569' }}
                >
                  {d}
                </div>
              ))}
            </div>
          </Bloc>
        </div>

        {/* Frontière gratuit / payant — explicite, c'est le point de la maquette */}
        <div className="flex items-center gap-[14px] px-5 sm:px-8">
          <div className="h-px flex-grow" style={{ backgroundColor: '#e2e8f0' }} />
          <div
            className="inline-flex shrink-0 items-center gap-[7px] text-[11.5px] font-semibold uppercase tracking-wide sm:text-[12.5px]"
            style={{ color: '#64748b' }}
          >
            <Lock size={13} />
            Fin de l'analyse gratuite
          </div>
          <div className="h-px flex-grow" style={{ backgroundColor: '#e2e8f0' }} />
        </div>

        {/* Plan d'action verrouillé */}
        <div
          className="relative mx-5 mb-6 mt-6 overflow-hidden rounded-2xl border sm:mx-8 sm:mb-8"
          style={{ borderColor: '#c7d2fe', backgroundColor: '#f5f3ff' }}
        >
          <div className="select-none px-6 py-6 sm:px-7" style={{ filter: 'blur(4.5px)', opacity: 0.55 }} aria-hidden="true">
            <div className="mb-3 text-[17px] font-bold" style={{ color: '#3730a3' }}>
              Votre plan d'action, étape par étape
            </div>
            <div className="text-[14.5px] leading-[1.7]" style={{ color: '#4338ca' }}>
              Étape 1 — Envoyez au cabinet une demande de communication des pièces justificatives par
              lettre recommandée. Un modèle prêt à poster est joint, avec les articles à citer et le
              délai de réponse à imposer. Étape 2 — Tant que la facture d'origine ne vous a pas été
              communiquée, ne procédez à aucun paiement même partiel&nbsp;: un versement, y compris de
              10&nbsp;€, peut valoir reconnaissance de la dette et faire repartir le délai de
              prescription à zéro. Étape 3 — Si le cabinet ne répond pas dans le délai imparti,
              adressez la mise en demeure de cesser le recouvrement.
            </div>
          </div>

          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-5 text-center"
            style={{
              background:
                'linear-gradient(180deg, rgba(245, 243, 255, 0.72) 0%, rgba(245, 243, 255, 0.96) 55%)',
            }}
          >
            <div
              className="flex h-11 w-11 items-center justify-center rounded-full border bg-white"
              style={{ borderColor: '#c7d2fe', boxShadow: '0 4px 10px -2px rgba(49, 46, 129, 0.14)' }}
            >
              <Lock size={19} style={{ color: '#4338ca' }} />
            </div>
            <div className="flex flex-col items-center gap-[5px]">
              <div className="text-base font-bold sm:text-[18px]" style={{ color: '#312e81' }}>
                Votre plan d'action, étape par étape
              </div>
              <div className="max-w-[460px] text-[13.5px] leading-normal sm:text-[14.5px]" style={{ color: '#4338ca' }}>
                Les courriers prêts à poster, les délais à respecter, et ce qu'il ne faut surtout pas
                faire.
              </div>
            </div>
            <button
              type="button"
              onClick={onCTAClick}
              className="inline-flex items-center gap-[10px] rounded-full px-6 py-[13px] text-[14.5px] font-semibold text-white transition-transform active:scale-[0.99] sm:px-[30px] sm:py-[15px] sm:text-base"
              style={{ backgroundColor: '#1e3a8a', boxShadow: '0 10px 15px -3px rgba(30, 58, 138, 0.24)' }}
            >
              Débloquer mon plan d'action — 9,99&nbsp;€
            </button>
            <div className="text-[11.5px] sm:text-[12.5px]" style={{ color: '#6366f1' }}>
              Paiement unique · Pas d'abonnement · Accessible immédiatement
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-2 text-center text-[12.5px] sm:text-[13px]" style={{ color: '#64748b' }}>
        <Info size={15} className="mt-[2px] shrink-0" />
        <span>
          Dossier fictif présenté à titre d'illustration. Cette analyse est un diagnostic, pas un
          verdict juridique.
        </span>
      </div>
    </div>
);

export default ApercuRapport;
